import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusStamp, SealStamp } from "@/components/StatusStamp";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Download, Send, UserCheck, UserX, Link2, MessageSquare, ShieldCheck, Copy, Tag, X, Upload, History as HistoryIcon } from "lucide-react";
import { TelegramIcon } from "@/components/icons/TelegramIcon";

const DIRECTION_LABEL = { incoming: "Входящий", outgoing: "Исходящий", internal: "Внутренний" };
const ACTION_LABEL = { created: "Документ создан", sent: "Отправлен на подписание", signed: "Подписан", rejected: "Отказано в подписи", version_uploaded: "Загружена новая версия файла" };
const SIGN_ROLES = ["owner", "director", "accountant"];

function fmtTime(ts) {
  return ts ? ts.slice(0, 16).replace("T", " ") : "";
}

export function DocumentDetailModal({ docId, open, onOpenChange, onChanged }) {
  const { user } = useAuth();
  const [doc, setDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [busy, setBusy] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [tagInput, setTagInput] = useState("");
  const [versionFile, setVersionFile] = useState(null);
  const [versionComment, setVersionComment] = useState("");
  const [uploadingVersion, setUploadingVersion] = useState(false);

  const canSign = SIGN_ROLES.includes(user?.role);
  const canAct = user?.role !== "viewer";

  const load = async () => {
    try {
      const { data } = await api.get(`/documents/${docId}`);
      setDoc(data);
      const { data: c } = await api.get(`/documents/${docId}/comments`);
      setComments(c);
    } catch {
      toast.error("Не удалось загрузить документ");
    }
  };

  useEffect(() => {
    if (open) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docId]);

  const act = async (fn, successMsg) => {
    setBusy(true);
    try {
      const { data } = await fn();
      toast.success(data?.message || successMsg);
      await load();
      onChanged?.();
      setShowReject(false);
      setRejectReason("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Операция не выполнена");
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    try {
      const { data } = await api.post(`/documents/${doc.id}/share`);
      const shareUrl = `${window.location.origin}/view/${data.share_token}`;
      window.open(shareUrl, "_blank", "noopener,noreferrer");
      toast.success("Ссылка для просмотра документа создана");
    } catch {
      toast.error("Не удалось создать ссылку для доступа");
    }
  };

  const sendComment = async () => {
    if (!commentText.trim()) return;
    try {
      await api.post(`/documents/${doc.id}/comments`, { text: commentText.trim() });
      setCommentText("");
      const { data: c } = await api.get(`/documents/${doc.id}/comments`);
      setComments(c);
    } catch {
      toast.error("Не удалось отправить комментарий");
    }
  };

  const saveTags = async (nextTags) => {
    try {
      await api.patch(`/documents/${doc.id}/tags`, { tags: nextTags });
      setDoc((d) => ({ ...d, tags: nextTags }));
    } catch {
      toast.error("Не удалось сохранить теги");
    }
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    saveTags([...new Set([...(doc.tags || []), tagInput.trim()])]);
    setTagInput("");
  };

  const removeTag = (t) => saveTags((doc.tags || []).filter((x) => x !== t));

  const copyVerifyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/verify/${doc.verification_code}`);
    toast.success("Ссылка проверки скопирована");
  };

  const uploadVersion = async () => {
    if (!versionFile) {
      toast.error("Выберите файл новой версии");
      return;
    }
    setUploadingVersion(true);
    try {
      const fd = new FormData();
      fd.append("file", versionFile);
      const { data: fileData } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post(`/documents/${doc.id}/versions`, { file: fileData, comment: versionComment.trim() || null });
      toast.success("Новая версия документа загружена");
      setVersionFile(null);
      setVersionComment("");
      await load();
      onChanged?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Хранилище файлов временно недоступно. Попробуйте позже", { duration: 6000 });
    } finally {
      setUploadingVersion(false);
    }
  };

  if (!doc) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl" data-testid="document-detail-modal">
          <VisuallyHidden asChild>
            <DialogTitle>Загрузка документа</DialogTitle>
          </VisuallyHidden>
          <div className="py-10 text-center text-slate-400 text-sm">Загрузка…</div>
        </DialogContent>
      </Dialog>
    );
  }

  const signedH = doc.history?.find((h) => h.action === "signed");
  const rejectedH = doc.history?.find((h) => h.action === "rejected");
  const signedMeta = { certificate: "01F29C8B0002AC19284D", actor: signedH?.actor_name, timestamp: fmtTime(signedH?.timestamp) };
  const rejectedMeta = { actor: rejectedH?.actor_name, reason: doc.reject_reason, timestamp: fmtTime(rejectedH?.timestamp) };
  const progressMeta = { actor: doc.created_by, timestamp: fmtTime(doc.history?.[0]?.timestamp) };

  const currentStepObj = doc.approval_route ? doc.approval_route[doc.current_step] : null;
  const isApprover = currentStepObj ? currentStepObj.approver_ids.includes(user?.id) : false;
  const alreadyApproved = currentStepObj ? currentStepObj.approved_by.some((a) => a.id === user?.id) : false;
  const canApproveNow = isApprover && !alreadyApproved && doc.status === "in_progress";
  const canActOnSignStep = doc.approval_route ? canApproveNow : canSign;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="document-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6">
            {doc.type} №{doc.number} <span className="text-slate-400 text-sm font-normal">от {doc.doc_date}</span>
          </DialogTitle>
        </DialogHeader>
        <Button
          size="icon"
          variant="ghost"
          title="Поделиться для изучения перед подписанием"
          onClick={handleShare}
          data-testid="share-doc-telegram-button"
          className="absolute right-10 top-4 text-[#229ED9] hover:bg-sky-50 hover:text-[#229ED9]"
        >
          <TelegramIcon className="w-4 h-4" />
        </Button>

        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-4 text-sm bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div>
              <div className="text-xs text-slate-400 uppercase">Направление</div>
              <div className="font-medium text-slate-800">{DIRECTION_LABEL[doc.direction]}</div>
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase">{doc.direction === "internal" ? "Участники" : "Контрагент"}</div>
              <div className="font-medium text-slate-800 flex items-center gap-1.5" data-testid="doc-counterparty-field">
                {doc.direction === "internal" ? "Внутри компании" : doc.counterparty_name || "—"}
                {doc.peer_doc_id && (
                  <span title="Реальный контрагент в системе КриптоДок" data-testid="real-link-indicator">
                    <Link2 className="w-3.5 h-3.5 text-emerald-600" />
                  </span>
                )}
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase">Сумма с НДС</div>
              <div className="font-mono font-medium text-slate-800">
                {doc.amount?.toLocaleString("ru-RU")} ₽ <span className="text-slate-400">(НДС {doc.vat?.toLocaleString("ru-RU")} ₽)</span>
              </div>
            </div>
            <div>
              <div className="text-xs text-slate-400 uppercase">Создал</div>
              <div className="font-medium text-slate-800">{doc.created_by}</div>
            </div>
            {doc.assigned_to_name && (
              <div className="col-span-2">
                <div className="text-xs text-slate-400 uppercase">Назначен</div>
                <div className="font-medium text-slate-800">{doc.assigned_to_name}</div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="doc-verification-code">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
            Код проверки: <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{doc.verification_code}</code>
            <button type="button" onClick={copyVerifyLink} data-testid="copy-verification-link-button" className="text-blue-600 hover:underline inline-flex items-center gap-1">
              <Copy className="w-3 h-3" /> Скопировать ссылку проверки
            </button>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase text-slate-400 mb-2 flex items-center gap-1.5">
              <Tag className="w-3.5 h-3.5" /> Теги
            </div>
            <div className="flex flex-wrap gap-1.5 mb-2" data-testid="doc-tags-list">
              {(doc.tags || []).map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs bg-slate-100 text-slate-700 rounded-full px-2.5 py-1" data-testid={`doc-tag-${t}`}>
                  {t}
                  {canAct && (
                    <button type="button" onClick={() => removeTag(t)} data-testid={`remove-tag-button-${t}`}>
                      <X className="w-3 h-3 text-slate-400 hover:text-rose-500" />
                    </button>
                  )}
                </span>
              ))}
              {(doc.tags || []).length === 0 && <span className="text-xs text-slate-400">Тегов нет</span>}
            </div>
            {canAct && (
              <div className="flex gap-2">
                <Input
                  placeholder="Добавить тег"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addTag()}
                  data-testid="add-tag-input"
                  className="h-8 text-sm"
                />
                <Button size="sm" onClick={addTag} data-testid="add-tag-button">Добавить</Button>
              </div>
            )}
          </div>

          <div data-testid="doc-status-stamp-area">
            <StatusStamp status={doc.status} className="mb-3" />
            {doc.status === "signed" && <SealStamp status="signed" meta={signedMeta} />}
            {doc.status === "in_progress" && <SealStamp status="in_progress" meta={progressMeta} />}
            {doc.status === "rejected" && <SealStamp status="rejected" meta={rejectedMeta} />}
          </div>

          {doc.approval_route && (
            <div data-testid="approval-steps-section">
              <div className="text-xs font-semibold uppercase text-slate-400 mb-2">Маршрут согласования</div>
              <div className="space-y-2">
                {doc.approval_route.map((step, idx) => {
                  const isCurrent = idx === doc.current_step && doc.status === "in_progress";
                  const isDone = step.status === "approved";
                  const isRejected = step.status === "rejected";
                  return (
                    <div
                      key={idx}
                      data-testid={`approval-step-${idx}`}
                      className={`rounded-lg border px-3 py-2 text-sm ${
                        isDone ? "border-blue-200 bg-blue-50" : isRejected ? "border-rose-200 bg-rose-50" : isCurrent ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"
                      }`}
                    >
                      <div className="font-medium text-slate-800">Этап {step.step}: {step.approver_names.join(", ")}</div>
                      <div className="text-xs text-slate-500">
                        {isDone && "Согласовано всеми участниками"}
                        {isRejected && `Отклонено: ${step.rejected_by?.name} — ${step.rejected_by?.reason}`}
                        {!isDone && !isRejected && `Согласовали: ${step.approved_by.map((a) => a.name).join(", ") || "никто"}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {doc.file && (
            <a
              href={`${API}/files/${doc.file.storage_path}`}
              target="_blank"
              rel="noreferrer"
              data-testid="doc-download-link"
              className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:underline"
            >
              <Download className="w-4 h-4" /> {doc.file.original_filename}
            </a>
          )}

          <div>
            <div className="text-xs font-semibold uppercase text-slate-400 mb-2 flex items-center gap-1.5">
              <HistoryIcon className="w-3.5 h-3.5" /> Версии документа
            </div>
            <div className="space-y-1.5 mb-2" data-testid="doc-versions-list">
              {[...(doc.versions || [])].reverse().map((v) => (
                <div key={v.id} className="flex items-center justify-between text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`doc-version-row-${v.version_no}`}>
                  <div>
                    <a href={`${API}/files/${v.storage_path}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline inline-flex items-center gap-1.5">
                      <Download className="w-3.5 h-3.5" /> Версия {v.version_no}: {v.original_filename}
                    </a>
                    <div className="text-xs text-slate-400">
                      {v.uploaded_by} — {fmtTime(v.uploaded_at)}{v.comment ? ` — ${v.comment}` : ""}
                    </div>
                  </div>
                </div>
              ))}
              {(doc.versions || []).length === 0 && <div className="text-xs text-slate-400">Файл пока не загружен</div>}
            </div>
            {canAct && doc.status !== "signed" && (
              <div className="flex gap-2 items-center">
                <label className="flex items-center gap-2 border border-dashed border-slate-300 rounded-lg px-3 py-2 text-xs text-slate-500 cursor-pointer hover:border-slate-400 flex-1">
                  <Upload className="w-3.5 h-3.5" />
                  {versionFile ? versionFile.name : "Выбрать файл новой версии"}
                  <input type="file" className="hidden" data-testid="new-version-file-input" onChange={(e) => setVersionFile(e.target.files?.[0] || null)} accept=".pdf,.png,.jpg,.jpeg" />
                </label>
                <Input
                  placeholder="Комментарий (опционально)"
                  value={versionComment}
                  onChange={(e) => setVersionComment(e.target.value)}
                  data-testid="new-version-comment-input"
                  className="h-8 text-xs max-w-[180px]"
                />
                <Button size="sm" onClick={uploadVersion} disabled={uploadingVersion || !versionFile} data-testid="upload-version-button">
                  Загрузить
                </Button>
              </div>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase text-slate-400 mb-2">История</div>
            <div className="space-y-2.5">
              {doc.history?.map((h, i) => (
                <div key={i} className="flex gap-3 text-sm" data-testid={`doc-history-entry-${i}`}>
                  <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                  <div>
                    <div className="text-slate-800">
                      {ACTION_LABEL[h.action] || h.action} — <span className="font-medium">{h.actor_name}</span>
                    </div>
                    {h.comment && <div className="text-xs text-slate-500">{h.comment}</div>}
                    <div className="text-xs text-slate-400">{fmtTime(h.timestamp)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {showReject && (
            <div className="space-y-2 border border-rose-200 bg-rose-50 rounded-lg p-3">
              <Textarea data-testid="reject-reason-input" placeholder="Укажите причину отказа" value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  disabled={!rejectReason || busy}
                  onClick={() => act(() => api.post(`/documents/${doc.id}/reject`, { reason: rejectReason }), "Отказ отправлен")}
                  className="bg-rose-600 hover:bg-rose-500"
                  data-testid="confirm-reject-button"
                >
                  Подтвердить отказ
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowReject(false)} data-testid="cancel-reject-button">
                  Отмена
                </Button>
              </div>
            </div>
          )}

          {canAct && (
            <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
              {doc.status === "draft" && (
                <Button
                  size="sm"
                  onClick={() => act(() => api.post(`/documents/${doc.id}/send`), "Документ отправлен на подписание")}
                  disabled={busy}
                  data-testid="send-doc-button"
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Отправить на подписание
                </Button>
              )}
              {doc.status === "in_progress" && (doc.direction === "incoming" || doc.direction === "internal") && canActOnSignStep && (
                <>
                  <Button
                    size="sm"
                    onClick={() => act(() => api.post(`/documents/${doc.id}/sign`), "Документ успешно подписан ЭЦП")}
                    disabled={busy}
                    data-testid="sign-doc-submit-button"
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                    {doc.approval_route ? `Согласовать (этап ${doc.current_step + 1})` : doc.direction === "internal" ? "Подписать" : "Подписать ЭЦП"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowReject(true)} disabled={busy} data-testid="reject-doc-button" className="border-rose-300 text-rose-600 hover:bg-rose-50">
                    <XCircle className="w-3.5 h-3.5 mr-1.5" /> {doc.approval_route ? "Отклонить этап" : doc.direction === "internal" ? "Отклонить" : "Отказать в подписи"}
                  </Button>
                </>
              )}
              {doc.status === "in_progress" && doc.direction === "outgoing" && (
                doc.peer_doc_id ? (
                  <p className="text-xs text-slate-500 w-full flex items-center gap-1.5">
                    <Link2 className="w-3.5 h-3.5 text-emerald-600" /> Документ отправлен реальному контрагенту в КриптоДок — ожидаем его решения
                  </p>
                ) : (
                  <>
                    <p className="text-xs text-slate-400 w-full">Демо-режим: симулируйте ответ контрагента</p>
                    <Button
                      size="sm"
                      onClick={() => act(() => api.post(`/documents/${doc.id}/simulate-counterparty?action=sign`), "Контрагент подписал документ")}
                      disabled={busy}
                      data-testid="simulate-counterparty-sign-button"
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      <UserCheck className="w-3.5 h-3.5 mr-1.5" /> Контрагент подписал
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(() => api.post(`/documents/${doc.id}/simulate-counterparty?action=reject`), "Контрагент отклонил документ")}
                      disabled={busy}
                      data-testid="simulate-counterparty-reject-button"
                      className="border-rose-300 text-rose-600 hover:bg-rose-50"
                    >
                      <UserX className="w-3.5 h-3.5 mr-1.5" /> Контрагент отказал
                    </Button>
                  </>
                )
              )}
            </div>
          )}

          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-semibold uppercase text-slate-400 mb-2 flex items-center gap-1.5">
              <MessageSquare className="w-3.5 h-3.5" /> Комментарии {doc.peer_doc_id && "(видны контрагенту)"}
            </div>
            <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
              {comments.map((c) => (
                <div key={c.id} className="text-sm bg-slate-50 rounded-lg px-3 py-2" data-testid={`doc-comment-${c.id}`}>
                  <span className="font-medium text-slate-800">{c.author_name}:</span> <span className="text-slate-600">{c.text}</span>
                  <div className="text-xs text-slate-400">{fmtTime(c.created_at)}</div>
                </div>
              ))}
              {comments.length === 0 && <div className="text-xs text-slate-400">Пока нет комментариев</div>}
            </div>
            <div className="flex gap-2">
              <Textarea
                data-testid="doc-comment-input"
                placeholder="Оставьте комментарий для коллеги или контрагента перед подписанием"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                className="min-h-[40px]"
              />
              <Button size="sm" onClick={sendComment} data-testid="doc-comment-send-button">Отправить</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
